import { computed, reactive, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { httpRequest } from '@/api/httpClient'
import { normalizeImageRefForServer } from '@/utils/normalizeImageRefForServer'

const IDB_NAME = 'zhipan_workbench'
const IDB_STORE = 'snapshots'
const IDB_KEY = 'workspace_state'

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function idbSave(data) {
  const raw = JSON.parse(JSON.stringify(data))
  const db = await openDB()
  const tx = db.transaction(IDB_STORE, 'readwrite')
  tx.objectStore(IDB_STORE).put(raw, IDB_KEY)
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

async function idbLoad() {
  const db = await openDB()
  const tx = db.transaction(IDB_STORE, 'readonly')
  const req = tx.objectStore(IDB_STORE).get(IDB_KEY)
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result || null)
    req.onerror = () => reject(req.error)
  })
}

const FIXED_IMAGE_SETTINGS = Object.freeze({
  model: 'doubao-seedream-4-5-251128',
  size: '2048x2048',
  watermark: true
})

const ratioOptions = [
  { value: '1:1', label: '1:1', size: '2048x2048' },
  { value: '4:3', label: '4:3', size: '2560x1920' },
  { value: '3:4', label: '3:4', size: '1920x2560' },
  { value: '16:9', label: '16:9', size: '2560x1440' },
  { value: '9:16', label: '9:16', size: '1440x2560' }
]


const compressImage = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = (event) => {
      const image = new Image()
      image.src = event.target.result
      image.onload = () => {
        let width = image.width
        let height = image.height
        const maxSize = 1024
        if (width > height) {
          if (width > maxSize) { height = Math.round((height * maxSize) / width); width = maxSize }
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height); height = maxSize
        }
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        canvas.getContext('2d').drawImage(image, 0, 0, width, height)
        resolve(canvas.toDataURL('image/jpeg', 0.7))
      }
      image.onerror = () => { console.warn('[compressImage] 图片加载失败'); resolve('') }
    }
    reader.onerror = () => { console.warn('[compressImage] FileReader 失败'); resolve('') }
  })

function createProduct(index) {
  return {
    id: Date.now() + index,
    name: `产品 ${String.fromCharCode(65 + index)}`,
    mainImage: '',
    mainPreview: '',
    detailImages: [],
    detailPreviews: []
  }
}

function createResultSlot() {
  return { url: '', loading: false, progress: 0, error: '', customPrompt: '', originalUrl: '' }
}

function createFissionColor(index) {
  return {
    id: Date.now() + index + Math.random(),
    label: `颜色 ${index + 1}`,
    refImage: '',
    refPreview: '',
    detailImage: '',
    detailPreview: '',
    results: [],
    loading: false
  }
}

const DEFAULT_PROMPTS = {
  promoSlot1: '美国电商风格产品摄影，图一为商品参考图，图二为模特参考图，请将图一中的商品穿在图二模特身上，生成模特正面全身照，展示服装整体效果，专业影棚灯光，产品居中构图',
  promoSlot2: '美国电商风格产品摄影，图一为商品参考图，图二为模特参考图，请将图一中的商品穿在图二模特身上，生成模特背面全身照，展示服装背部设计和剪裁，专业影棚灯光，产品居中构图',
  promoSlot3: '美国电商风格产品摄影，图一为商品参考图，图二为模特参考图，请将图一中的商品穿在图二模特身上，生成模特侧面全身照，展示服装侧面轮廓和版型，专业影棚灯光，产品居中构图',
  promoSlot4: '美国电商风格产品摄影，图一为商品参考图，图二为模特参考图，请将图一中的商品穿在图二模特身上，生成模特45度斜角构图，展现服装立体感和层次，专业影棚灯光，产品居中构图',
  promoSlot5: '美国电商风格产品摄影，图一为商品参考图，图二为模特参考图，请将图一中的商品穿在图二模特身上，生成模特上半身特写，展示领口设计和面料质感，专业影棚灯光，产品居中构图',
  promoSlot6: '美国电商风格产品摄影，服装细节面料特写，展示缝线、纹理和材质细节，专业影棚灯光，产品居中构图',
  simpleModePrompt: '简约纯色背景，干净明亮，突出衣服本身的质感和氛围，背景必须干净高级，不能荧光色，符合美国消费者审美。',
  simpleBgColors: '白色|浅米色|浅灰色|浅粉色|深粉色|深咖色|深米色|深灰色|黑色',
  lifestyleModePrompt: '美式生活场景背景，北美真实居家或户外使用场景，自然阳光，温馨舒适的生活方式，模特自然展示产品，符合美国主流审美',
  hardConstraints: '。【硬性约束：1. 模特的长相、肤色、发型、身材必须与模特参考图（图二）完全一致，严禁自行改变模特外貌！2. 下半身必须有穿着，不能光腿；3. 画面中只能有唯一一个人物，严禁出现多个人物！严禁生成多组拼图或多格分镜！4. 照片要有实拍感，整体打光明亮不昏暗，色彩饱和度高，不要像AI作图！5. 必须符合美国审美标准】',
  fissionPrompt: '请提取图二中的商品，分析商品是什么，精准嵌入到图一的场景中。保持商品外形、材质和颜色完全不变。图一的背景环境、模特及动作保持原封不动。确保商品与场景的光影融合自然，无违和感。',
  fissionDetailPrompt: '。图三为商品细节参考图，请在保持商品颜色、材质、图案、纹理等细节与图三完全一致的前提下完成迁移',
  removeBgPrompt: '分析商品是什么，抠图并保留完整商品主体，重绘背景：商品置于纯白背景，柔和阴影，电商主图，不要界面元素！只要商品抠图！',
  copywritingPrompt: `你是一个资深的亚马逊美国站文案撰写师，请根据我每次上传图片的产品的热卖点，亚马逊的cosmo推荐算法，撰写高水平亚马逊英文listing文案。

现在以女装类目下秋冬季产品cardigan sweater为例，展示服装类产品listing的写作规范和要求。请你学习以下书写要求和逻辑，将之沿用到其他服装类产品和其他类目产品的listing写作上。根据不同情况，替换具体词汇表达。
硬性要求：
只有类似cashmere, wool, cotton等材质，才把材质作为卖点放进标题、五点和大描述。普通Polyester等材质，不作为卖点在listing中出现（弱化表达）。
不要出现新疆棉、抗菌、除菌、杀虫、Sexy、Sassy、成人内容、品牌词、夸张保证、refund guarantee。

【输出格式强制要求（不可违背）】:
绝对不要输出任何Markdown语法（禁止使用代码块包裹）。直接输出以 { 开头，以 } 结尾的纯合法JSON字符串，包含且仅包含以下Key：
title(纯英文标题), titleZh(纯中文标题翻译), highlights(纯英文商品亮点), introduction(纯英文大描述), introductionZh(纯中文大描述翻译), features(纯英文五点描述数组5条), featuresZh(纯中文五点翻译数组5条), keywords(纯英文逗号分隔关键词)

以下用cardigan sweater为例。
1.标题：只保留前75个字符，不能超过75个字符！结构：Women/Womens +核心特征词+核心产品词+季节特征词+其他特征词+场景词。核心特征词是买家用来定义产品的词；其他特征词是帮助转化和补充识别的细节词。示例：Womens Cable Knit Oversize Sweater Fall Winter Pullover Crewneck Cardigan

2.五点描述：每点150-230字符，分布均匀。按面料体感、设计细节、搭配方式、场景、尺码洗护购买提示展开。每点开头用3-4个单词的小标题引领，小标题和内容之间用英文冒号连接。第五点多尺码提醒If between sizes or prefer extra layering room, consider sizing up；均码提醒This one-size style is designed for a flexible, easy fit.

3.大描述：1200-1500字符。参考结构Opening Hook-Soft Knit Feel-Designed to Flatter-Easy to Style-Made for Everyday Plans-Fit and Care Note，结尾句soft CTA呼应开头。语言像美国女装消费者能听懂的穿搭建议，不要像工厂说明书。手机端排版：短段落+小标题+每段一个重点，小标题加粗单独成行，段间空一行。

4.商品亮点(Highlights)：125个字符以内，由英文逗号分隔的短语组成，补充标题写不下的关键产品特征和差异化卖点，不能写主观评价。从图片提取客观可验证的特征：材质、结构设计、版型、功能细节、场景。示例：Chiffon fabric, halter neckline, lace trim hem, asymmetrical silhouette, back button closure, sleeveless, vacation, date night

5.关键词：250个字符以内，读取标题排除已出现的主要词，补充同义词、风格词、场景词、搭配词、人群词、季节词。全部小写英文，单词间用空格隔开，不加标点。`,
  sizeChartImagePromptTemplate: '你是一个专业的跨境电商视觉设计师。请以图生图重绘这张中文尺码图，严格按照以下要求进行彻底的本地化（英文）改造：\n\n【核心任务：彻底英化与数据替换】\n1. 全图无中文（极度重要）：原图中的所有中文必须 100% 全部清除，重绘后的画面中绝对不能出现任何一个汉字！\n2. 精准数据替换：请使用以下最新英文数据（inch/lb）重绘表格，必须严格按照此 JSON 结构和数值渲染，不可遗漏或拼错任何一个字母与数字：\n${DATA_PLACEHOLDER}\n3. 视觉排版：保持原图的排版风格，确保行列数据精准对齐，文字清晰锐利，不能比原图表格添加行列。\n\n【附加任务：防退货温馨提示】\n4. 底部嵌入提示：请在生成的尺码图最底部正中、显眼的位置，清晰地印上这句英文防退货提示：\n"Please carefully check the size chart. If you are unsure, please choose one size larger."'
}

const prompts = reactive({ ...DEFAULT_PROMPTS })

const loadPrompts = async () => {
  try {
    const data = await httpRequest('/api/admin/prompts', { method: 'GET' })
    if (data && typeof data === 'object') {
      for (const key of Object.keys(data)) {
        if (typeof data[key] === 'string') prompts[key] = data[key]
      }
    }
  } catch (e) {
    console.warn('[loadPrompts] 使用默认提示词:', e.message)
  }
}
loadPrompts()

const MOCK_MODE = false
const INPUT_IMAGE_SENSITIVE_CODE = 'InputImageSensitiveContentDetected'
const mockImageUrl = (seed) => `https://picsum.photos/seed/${seed}/600/600`
const mockCopyResult = {
  title: 'Premium Wireless Bluetooth Earbuds with Noise Cancelling, 36H Playtime, IPX7 Waterproof, Hi-Fi Stereo Sound with Deep Bass, Built-in Microphone for Sports/Workout/Running',
  bullets: [
    'Active Noise Cancellation: Block out ambient noise and immerse yourself in crystal-clear audio with our advanced ANC technology, perfect for commuting, office work, and travel.',
    '36-Hour Battery Life: Enjoy up to 8 hours of continuous playback on a single charge, with the compact charging case providing an additional 28 hours of power.',
    'IPX7 Waterproof Rating: Designed to withstand sweat, rain, and splashes, making these earbuds perfect for intense workouts, running, and outdoor activities.',
    'Hi-Fi Stereo Sound with Deep Bass: Equipped with 13mm dynamic drivers that deliver rich, detailed sound with punchy bass and crystal-clear highs for an immersive listening experience.',
    'One-Step Pairing & Touch Control: Simply take the earbuds out of the case and they automatically connect to your last paired device. Intuitive touch controls let you manage music, calls, and voice assistants with ease.'
  ],
  description: 'Experience premium sound quality without breaking the bank. Our Wireless Bluetooth Earbuds combine cutting-edge audio technology with a comfortable, ergonomic design that stays secure during any activity. The advanced Bluetooth 5.3 chip ensures stable, lag-free connection with your smartphone, tablet, or laptop. Whether you are working out, commuting, or relaxing at home, these earbuds deliver the perfect soundtrack to your day. The compact charging case fits easily in your pocket, so your music is always ready when you are.',
  keywords: 'wireless earbuds, bluetooth earbuds, noise cancelling earbuds, waterproof earbuds, sports earbuds, running headphones, bluetooth headphones, earbuds with microphone, deep bass earbuds, IPX7 earbuds'
}

// ═══════════════════════════════════════════════════════════
// Main workbench store
// ═══════════════════════════════════════════════════════════
export const useWorkbenchStore = defineStore('workbench', () => {
  const settings = reactive({ ...FIXED_IMAGE_SETTINGS })

  const sensitiveContentWarningVisible = ref(false)
  const showSensitiveContentWarning = () => { sensitiveContentWarningVisible.value = true }
  const closeSensitiveContentWarning = () => { sensitiveContentWarningVisible.value = false }
  const createImageGenerationError = (data, fallbackMessage) => {
    const code = data?.code || ''
    const message = data?.error || fallbackMessage
    if (code === INPUT_IMAGE_SENSITIVE_CODE || String(message).includes(INPUT_IMAGE_SENSITIVE_CODE)) {
      showSensitiveContentWarning()
      return new Error('图片包含敏感内容，请更换图片或调整提示词')
    }
    return new Error(message)
  }

  const isWomenClothing = ref(true)
  const setIsWomenClothing = (v) => { isWomenClothing.value = !!v; saveState() }

  let _saveStateFn = null
  let _restoring = false
  const exportImageOverrides = reactive([{}, {}, {}])

  const requestRaw = async (path, options = {}) => {
    const response = await httpRequest(path, { ...options, responseType: 'raw', throwOnHttpError: false })
    return response
  }

  // 图片生成服务返回的是短期签名 URL。生成成功后立刻通过后端读取图片并转成
  // data URI，再写入 IndexedDB，避免链接过期后浏览器缓存中的图片损坏。
  const persistGeneratedImage = async (rawRef) => {
    if (typeof rawRef !== 'string' || !rawRef.trim()) return ''
    const imageRef = rawRef.trim()
    if (imageRef.startsWith('data:')) return imageRef
    if (!imageRef.startsWith('https://')) return imageRef

    const response = await requestRaw('/api/image-to-base64', {
      method: 'POST',
      body: { url: imageRef }
    })
    const data = await response.json()
    if (!response.ok || !data?.imageBase64) {
      throw new Error(data?.error || '生成图片本地保存失败')
    }
    return data.imageBase64
  }

  // ─── Products (shared across all views) ───
  const products = reactive([])

  const initProducts = () => {
    if (products.length === 0) {
      for (let i = 0; i < 3; i++) products.push(createProduct(i))
    }
  }
  initProducts()

  const setProductMainImage = async (productId, file) => {
    const p = products.find(p => p.id === productId)
    if (!p || !file) return
    if (p.mainPreview) URL.revokeObjectURL(p.mainPreview)
    p.mainPreview = URL.createObjectURL(file)
    p.mainImage = await compressImage(file)
    saveState()
  }

  const clearProductMainImage = (productId) => {
    const p = products.find(p => p.id === productId)
    if (!p) return
    if (p.mainPreview) URL.revokeObjectURL(p.mainPreview)
    p.mainImage = ''
    p.mainPreview = ''
    saveState()
  }

  const addProductDetailImages = async (productId, files) => {
    const p = products.find(p => p.id === productId)
    if (!p) return
    for (const file of files) {
      const preview = URL.createObjectURL(file)
      const compressed = await compressImage(file)
      p.detailPreviews.push(preview)
      p.detailImages.push(compressed)
    }
    saveState()
  }

  const removeProductDetailImage = (productId, index) => {
    const p = products.find(p => p.id === productId)
    if (!p) return
    if (p.detailPreviews[index]) URL.revokeObjectURL(p.detailPreviews[index])
    p.detailPreviews.splice(index, 1)
    p.detailImages.splice(index, 1)
    saveState()
  }

  // ─── Image Generation ───
  const imageGen = reactive({
    modes: ['', '', ''],
    step: 2,
    genResults: [[], [], []],
    genCounts: [6, 6, 6],
    fissionProductId: null,
    fissionColors: [[], [], []]
  })

  // ─── Model reference images (one per product, shared across first 5 slots) ───
  const selectedModelImages = reactive(['', '', ''])
  const selectedSimpleBgColors = reactive(['', '', ''])

  const fetchRandomModelImage = async () => {
    try {
      const res = await requestRaw('/api/model-images/random')
      if (!res.ok) return ''
      const data = await res.json()
      return data.imageBase64 || ''
    } catch (e) {
      console.warn('[fetchRandomModelImage] 获取模特素材失败:', e.message)
      return ''
    }
  }

  const lightbox = reactive({ show: false, url: '', images: [], index: 0 })
  const openLightbox = (url, images) => {
    if (!url) return
    lightbox.images = images || [url]
    lightbox.index = lightbox.images.indexOf(url)
    if (lightbox.index < 0) lightbox.index = 0
    lightbox.url = url
    lightbox.show = true
  }
  const closeLightbox = () => { lightbox.show = false; lightbox.url = ''; lightbox.images = []; lightbox.index = 0 }
  const lightboxPrev = () => {
    if (lightbox.index > 0) {
      lightbox.index--
      lightbox.url = lightbox.images[lightbox.index]
    }
  }
  const lightboxNext = () => {
    if (lightbox.index < lightbox.images.length - 1) {
      lightbox.index++
      lightbox.url = lightbox.images[lightbox.index]
    }
  }

  const setProductMode = (productIndex, mode) => {
    imageGen.modes[productIndex] = mode
  }

  const initGenResults = () => {
    for (let i = 0; i < 3; i++) {
      const count = imageGen.genCounts[i]
      if (!imageGen.genResults[i] || imageGen.genResults[i].length === 0) {
        imageGen.genResults[i] = Array.from({ length: count }, createResultSlot)
      }
    }
  }
  initGenResults()

  const clearImageSlot = (productIndex, slotIndex) => {
    const slot = imageGen.genResults[productIndex]?.[slotIndex]
    if (!slot) return
    slot.url = ''
    slot.error = ''
    slot.loading = false
    slot.progress = 0
    const selIdx = selectedFissionSlots.findIndex(s => s.pi === productIndex && s.si === slotIndex)
    if (selIdx >= 0) selectedFissionSlots.splice(selIdx, 1)
    const promoIdx = selectedPromoForMain.findIndex(s => s.pi === productIndex && s.si === slotIndex)
    if (promoIdx >= 0) selectedPromoForMain.splice(promoIdx, 1)
    saveState()
  }

  const setGenCount = (productIndex, count) => {
    const newCount = Math.max(1, Math.min(6, count))
    const results = imageGen.genResults[productIndex]
    if (!results) return
    if (newCount > results.length) {
      for (let i = results.length; i < newCount; i++) results.push(createResultSlot())
    } else if (newCount < results.length) {
      results.splice(newCount)
    }
    imageGen.genCounts[productIndex] = newCount
  }

  const getModePrompt = (productIndex) => {
    const mode = imageGen.modes[productIndex]
    const suffix = isWomenClothing.value ? '' : '_general'
    if (mode !== 'simple') return prompts['lifestyleModePrompt' + suffix] || prompts.lifestyleModePrompt
    const base = prompts['simpleModePrompt' + suffix] || prompts.simpleModePrompt
    const raw = prompts.simpleBgColors || ''
    const colors = raw.split('|').map(s => s.trim()).filter(Boolean)
    if (colors.length === 0) return base
    let color = selectedSimpleBgColors[productIndex]
    if (!color || !colors.includes(color)) {
      color = colors[Math.floor(Math.random() * colors.length)]
      selectedSimpleBgColors[productIndex] = color
      saveState()
    }
    return `${base} 本次背景颜色：${color}。`
  }

  const getSlotPrompt = (productIndex, slotIndex) => {
    const baseKey = 'promoSlot' + (slotIndex + 1)
    const slotKey = isWomenClothing.value ? baseKey : baseKey + '_general'
    const slotPrompt = prompts[slotKey] || prompts[baseKey] || ''
    const modePrompt = getModePrompt(productIndex)
    const hardKey = isWomenClothing.value ? 'hardConstraints' : 'hardConstraints_general'
    return slotPrompt + modePrompt + (prompts[hardKey] || prompts.hardConstraints)
  }

  const generateOneImageSlot = async (productIndex, slotIndex, modelImageBase64) => {
    const product = products[productIndex]
    if (!product) return
    if (!MOCK_MODE && !product.mainImage) return
    if (!imageGen.modes[productIndex]) return
    const results = imageGen.genResults[productIndex]
    if (!results) return
    const slot = results[slotIndex]
    if (!slot) return
    let effectiveModelImage = ''
    if (isWomenClothing.value) {
      effectiveModelImage = modelImageBase64 || selectedModelImages[productIndex] || ''
      if (!effectiveModelImage && slotIndex < 5) {
        effectiveModelImage = await fetchRandomModelImage()
        if (effectiveModelImage) {
          selectedModelImages[productIndex] = effectiveModelImage
        }
      }
    }
    const selIdx = selectedFissionSlots.findIndex(s => s.pi === productIndex && s.si === slotIndex)
    if (selIdx >= 0) selectedFissionSlots.splice(selIdx, 1)
    const promoIdx = selectedPromoForMain.findIndex(s => s.pi === productIndex && s.si === slotIndex)
    if (promoIdx >= 0) selectedPromoForMain.splice(promoIdx, 1)
    slot.loading = true
    slot.error = ''
    slot.progress = 0

    await loadPrompts()
    const promptText = getSlotPrompt(productIndex, slotIndex)

    const duration = 20000
    const startTimestamp = performance.now()
    let resolved = false
    let finished = false
    let pendingUrl = ''

    const finalizeSuccess = () => {
      if (finished) return
      finished = true
      clearInterval(timer)
      slot.progress = 100
      slot.url = pendingUrl
      slot.loading = false
      saveState()
    }

    const timer = setInterval(() => {
      const elapsed = performance.now() - startTimestamp
      const linearProgress = Math.min(99, (elapsed / duration) * 99)
      if (!resolved) slot.progress = linearProgress
      else if (elapsed >= duration) finalizeSuccess()
      else slot.progress = linearProgress
    }, 100)

    try {
      if (MOCK_MODE) {
        await new Promise(r => setTimeout(r, 2000 + Math.random() * 3000))
        pendingUrl = mockImageUrl(`gen-${productIndex}-${slotIndex}-${Date.now()}`)
      } else {
        settings.model = 'doubao-seedream-5-0-260128'
        const body = {
          model: settings.model,
          prompt: promptText,
          size: '2048x2048',
          watermark: settings.watermark,
          imageBase64: product.mainImage
        }
        // 前5张使用模特参考图（slotIndex 0-4），第6张为细节图不需要模特
        if (slotIndex < 5 && effectiveModelImage) {
          body.modelImageBase64 = effectiveModelImage
        }
        const response = await requestRaw('/api/image', {
          method: 'POST',
          body
        })
        const data = await response.json()
        if (!response.ok) {
          if (data?.code === INPUT_IMAGE_SENSITIVE_CODE || String(data?.error || '').includes(INPUT_IMAGE_SENSITIVE_CODE)) {
            throw createImageGenerationError(data, '生成失败')
          }
          if (response.status === 402) throw new Error('算力不足或未登录，请充值后重试！')
          throw createImageGenerationError(data, '生成失败')
        }

        const first = Array.isArray(data?.data) ? data.data[0] : data?.data?.[0]
        const generatedUrl = first?.url || (first?.b64_json ? `data:image/png;base64,${first.b64_json}` : '')
        pendingUrl = await persistGeneratedImage(generatedUrl)
      }
      resolved = true
      if (performance.now() - startTimestamp >= duration) finalizeSuccess()
    } catch (error) {
      slot.error = error.message || '生成失败'
      slot.progress = 0
      clearInterval(timer)
      slot.loading = false
    } finally {
      if (!resolved && !slot.error) {
        clearInterval(timer)
        slot.loading = false
      }
    }
  }

  const generateAllSlots = async (productIndex) => {
    const results = imageGen.genResults[productIndex]
    if (!results) return
    let modelImage = selectedModelImages[productIndex]
    if (!modelImage) {
      modelImage = await fetchRandomModelImage()
      if (modelImage) {
        selectedModelImages[productIndex] = modelImage
      }
    }
    await Promise.all(results.map((_, i) => generateOneImageSlot(productIndex, i, modelImage)))
  }

  const generateAllProducts = async () => {
    for (let i = 0; i < products.length; i++) {
      if (products[i]?.mainImage && !selectedModelImages[i]) {
        selectedModelImages[i] = await fetchRandomModelImage()
      }
    }
    await Promise.all(products.map((p, i) => {
      if (p?.mainImage) return generateAllSlots(i)
    }))
  }

  // ─── Fission ───
  const selectedFissionSlots = reactive([])
  const customPromoImages = reactive([])

  const sortFissionSlots = () => selectedFissionSlots.sort((a, b) =>
    a.pi - b.pi || (a.customIdx ?? -1) - (b.customIdx ?? -1) || a.si - b.si)

  const ensureCustomPromoSlot = (pi) => {
    if (!customPromoImages[pi]) customPromoImages[pi] = []
    return customPromoImages[pi]
  }

  const addCustomPromoImage = async (pi, file) => {
    ensureCustomPromoSlot(pi)
    const compressed = await compressImage(file)
    const customIdx = customPromoImages[pi].length
    customPromoImages[pi].push({ url: compressed, preview: compressed, originalUrl: '' })
    selectedFissionSlots.push({ pi, customIdx, key: `${pi}-custom-${customIdx}` })
    sortFissionSlots()
    saveState()
  }

  const applyCropToCustomPromoImage = (pi, customIdx, croppedBase64) => {
    const img = customPromoImages[pi]?.[customIdx]
    if (!img) return
    if (!img.originalUrl) img.originalUrl = img.url
    img.url = croppedBase64
    img.preview = croppedBase64
    saveState()
  }

  const restoreCustomPromoImage = (pi, customIdx) => {
    const img = customPromoImages[pi]?.[customIdx]
    if (!img?.originalUrl) return
    img.url = img.originalUrl
    img.preview = img.originalUrl
    img.originalUrl = ''
    saveState()
  }

  const removeCustomPromoImage = (pi, customIdx) => {
    const arr = customPromoImages[pi] || []
    if (customIdx < 0 || customIdx >= arr.length) return
    arr.splice(customIdx, 1)
    for (let i = selectedFissionSlots.length - 1; i >= 0; i--) {
      const s = selectedFissionSlots[i]
      if (s.pi !== pi || typeof s.customIdx !== 'number') continue
      if (s.customIdx === customIdx) {
        selectedFissionSlots.splice(i, 1)
      } else if (s.customIdx > customIdx) {
        s.customIdx -= 1
        s.key = `${pi}-custom-${s.customIdx}`
      }
    }
    if (arr.length === 0) customPromoImages[pi] = []
    saveState()
  }

  const getCustomPromoImageUrl = (pi, customIdx) => {
    return customPromoImages[pi]?.[customIdx]?.url || ''
  }

  const toggleSlotSelection = (pi, si) => {
    const key = `${pi}-${si}`
    const idx = selectedFissionSlots.findIndex(s => s.pi === pi && s.si === si)
    if (idx >= 0) selectedFissionSlots.splice(idx, 1)
    else selectedFissionSlots.push({ pi, si, key })
    sortFissionSlots()
  }

  const isSlotSelected = (pi, si) => selectedFissionSlots.some(s => s.pi === pi && s.si === si)

  const getSelectedSlotCount = (pi) => selectedFissionSlots.filter(s => s.pi === pi).length

  const selectAllSlots = (pi) => {
    const results = imageGen.genResults[pi]
    if (!results) return
    const allSelected = results.every((_, si) => isSlotSelected(pi, si))
    if (allSelected) {
      for (let i = selectedFissionSlots.length - 1; i >= 0; i--) {
        if (selectedFissionSlots[i].pi === pi) selectedFissionSlots.splice(i, 1)
      }
    } else {
      const existing = new Set(selectedFissionSlots.filter(s => s.pi === pi).map(s => s.si))
      for (let si = 0; si < results.length; si++) {
        if (!existing.has(si)) selectedFissionSlots.push({ pi, si, key: `${pi}-${si}` })
      }
    }
    sortFissionSlots()
    saveState()
  }

  const isAllSlotsSelected = (pi) => {
    const results = imageGen.genResults[pi]
    if (!results || results.length === 0) return false
    return results.every((_, si) => isSlotSelected(pi, si))
  }

  const enterFissionFromSelection = () => {
    const productIndices = [...new Set(selectedFissionSlots.map(s => s.pi))]
    if (productIndices.length === 0) return
    imageGen.step = 3
    for (const pi of productIndices) {
      const slotCount = getSelectedSlotCount(pi)
      if (!imageGen.fissionColors[pi] || imageGen.fissionColors[pi].length === 0) {
        imageGen.fissionColors[pi] = [createFissionColor(0), createFissionColor(1)]
      }
      for (const color of imageGen.fissionColors[pi]) {
        if (color.results.length !== slotCount) {
          const newResults = Array.from({ length: slotCount }, (_, i) =>
            color.results[i] || createResultSlot()
          )
          color.results = newResults
        }
      }
    }
  }

  const addFissionColor = (productIndex) => {
    const colors = imageGen.fissionColors[productIndex]
    if (!colors) return
    colors.push(createFissionColor(colors.length))
    saveState()
  }

  const removeFissionColor = (productIndex, colorIndex) => {
    const colors = imageGen.fissionColors[productIndex]
    if (!colors || colors.length <= 1) return
    delete mainImageGen.results[getMainImageKey(productIndex, colorIndex)]
    for (let i = selectedFissionImages.length - 1; i >= 0; i--) {
      const selected = selectedFissionImages[i]
      if (selected.pi !== productIndex) continue
      if (selected.ci === colorIndex) selectedFissionImages.splice(i, 1)
      else if (selected.ci > colorIndex) selected.ci--
    }
    colors.splice(colorIndex, 1)
    saveState()
  }

  const setFissionColorRef = async (productIndex, colorIndex, file) => {
    const colors = imageGen.fissionColors[productIndex]
    if (!colors || !colors[colorIndex]) return
    const color = colors[colorIndex]
    if (color.refPreview) URL.revokeObjectURL(color.refPreview)
    color.refPreview = URL.createObjectURL(file)
    color.refImage = await compressImage(file)
    saveState()
  }

  const setFissionColorDetail = async (productIndex, colorIndex, file) => {
    const colors = imageGen.fissionColors[productIndex]
    if (!colors || !colors[colorIndex]) return
    const color = colors[colorIndex]
    if (color.detailPreview) URL.revokeObjectURL(color.detailPreview)
    color.detailPreview = URL.createObjectURL(file)
    color.detailImage = await compressImage(file)
    saveState()
  }

  const generateOneFission = async (productIndex, colorIndex, resultIndex) => {
    const colors = imageGen.fissionColors[productIndex]
    if (!colors) return
    const color = colors[colorIndex]
    if (!color) return
    if (!MOCK_MODE && !color.refImage) return
    if (!color.results[resultIndex]) {
      color.results[resultIndex] = { url: '', loading: false, progress: 0, error: '' }
    }
    const slot = color.results[resultIndex]
    const selIdx = selectedFissionImages.findIndex(s => s.pi === productIndex && s.ci === colorIndex && s.ri === resultIndex)
    if (selIdx >= 0) selectedFissionImages.splice(selIdx, 1)
    const mainKey = getMainImageKey(productIndex, colorIndex)
    delete mainImageGen.results[mainKey]
    slot.loading = true
    slot.error = ''
    slot.progress = 0

    const product = products[productIndex]
    const selectedSlots = selectedFissionSlots.filter(s => s.pi === productIndex)
    const sourceSlot = selectedSlots[resultIndex]
    let sourceUrl = ''
    if (sourceSlot) {
      if (typeof sourceSlot.customIdx === 'number') {
        sourceUrl = getCustomPromoImageUrl(productIndex, sourceSlot.customIdx)
      } else {
        sourceUrl = imageGen.genResults[productIndex]?.[sourceSlot.si]?.url
      }
    }
    if (!sourceUrl) sourceUrl = imageGen.genResults[productIndex]?.[0]?.url || product?.mainImage
    if (!MOCK_MODE && !sourceUrl) { slot.loading = false; slot.error = '请先生成宣传图'; return }

    const duration = 20000
    const startTs = performance.now()
    let resolved = false
    let finished = false
    let pendingUrl = ''

    const finalizeSuccess = () => {
      if (finished) return
      finished = true
      clearInterval(timer)
      slot.progress = 100
      slot.url = pendingUrl
      slot.loading = false
      saveState()
    }

    const timer = setInterval(() => {
      const elapsed = performance.now() - startTs
      const p = Math.min(99, (elapsed / duration) * 99)
      if (!resolved) slot.progress = p
      else if (elapsed >= duration) finalizeSuccess()
      else slot.progress = p
    }, 100)

    try {
      if (MOCK_MODE) {
        await new Promise(r => setTimeout(r, 2000 + Math.random() * 3000))
        pendingUrl = mockImageUrl(`fission-${productIndex}-${colorIndex}-${resultIndex}-${Date.now()}`)
      } else {
        await loadPrompts()
        const normalizedBaseImage = await normalizeImageRefForServer(sourceUrl)
        const hasDetail = !!color.detailImage
        const body = {
          model: settings.model,
          prompt: hasDetail ? prompts.fissionPrompt + (prompts.fissionDetailPrompt || '') : prompts.fissionPrompt,
          size: '2048x2048',
          watermark: settings.watermark,
          baseImage: normalizedBaseImage,
          productImage: color.refImage
        }
        if (hasDetail) body.detailImage = color.detailImage
        const response = await requestRaw('/api/fission', {
          method: 'POST',
          body
        })
        const data = await response.json()
        if (!response.ok) {
          if (data?.code === INPUT_IMAGE_SENSITIVE_CODE || String(data?.error || '').includes(INPUT_IMAGE_SENSITIVE_CODE)) {
            throw createImageGenerationError(data, '裂变失败')
          }
          if (response.status === 402) throw new Error('算力不足')
          throw createImageGenerationError(data, '裂变失败')
        }
        const first = Array.isArray(data?.data) ? data.data[0] : data?.data?.[0]
        const generatedUrl = first?.url || (first?.b64_json ? `data:image/png;base64,${first.b64_json}` : '')
        pendingUrl = await persistGeneratedImage(generatedUrl)
      }
      resolved = true
      if (performance.now() - startTs >= duration) finalizeSuccess()
    } catch (e) {
      slot.error = e.message || '裂变失败'
      slot.progress = 0
      clearInterval(timer)
      slot.loading = false
    } finally {
      if (!resolved && !slot.error) {
        clearInterval(timer)
        slot.loading = false
      }
    }
  }

  const generateAllFissionForColor = async (productIndex, colorIndex) => {
    const color = imageGen.fissionColors[productIndex]?.[colorIndex]
    if (!color || !color.refImage) return
    const slotCount = getSelectedSlotCount(productIndex)
    if (color.results.length !== slotCount) {
      const newResults = Array.from({ length: slotCount }, (_, i) =>
        color.results[i] || createResultSlot()
      )
      color.results = newResults
    }
    await Promise.all(color.results.map((_, i) => generateOneFission(productIndex, colorIndex, i)))
  }

  const generateAllFissionForProduct = async (productIndex) => {
    const colors = imageGen.fissionColors[productIndex]
    if (!colors) return
    await Promise.all(colors.map((c, i) => {
      if (c.refImage) return generateAllFissionForColor(productIndex, i)
    }))
  }

  // ─── Fission image selection for main image gen ───
  const selectedFissionImages = reactive([])

  const selectedPromoForMain = reactive([])

  const togglePromoForMain = (pi, si) => {
    const idx = selectedPromoForMain.findIndex(s => s.pi === pi && s.si === si)
    if (idx >= 0) selectedPromoForMain.splice(idx, 1)
    else selectedPromoForMain.push({ pi, si })
  }

  const isPromoSelectedForMain = (pi, si) => selectedPromoForMain.some(s => s.pi === pi && s.si === si)

  const toggleFissionImageSelection = (pi, ci, ri) => {
    const idx = selectedFissionImages.findIndex(s => s.pi === pi && s.ci === ci && s.ri === ri)
    if (idx >= 0) selectedFissionImages.splice(idx, 1)
    else selectedFissionImages.push({ pi, ci, ri })
  }

  const clearFissionResult = (pi, ci, ri) => {
    const slot = imageGen.fissionColors[pi]?.[ci]?.results?.[ri]
    if (!slot) return
    slot.url = ''
    slot.error = ''
    slot.loading = false
    slot.progress = 0
    const selIdx = selectedFissionImages.findIndex(s => s.pi === pi && s.ci === ci && s.ri === ri)
    if (selIdx >= 0) selectedFissionImages.splice(selIdx, 1)
    const key = getMainImageKey(pi, ci)
    clearMainImageResult(key)
  }

  const isFissionImageSelected = (pi, ci, ri) => {
    return selectedFissionImages.some(s => s.pi === pi && s.ci === ci && s.ri === ri)
  }

  const selectedFissionImageCount = computed(() => selectedFissionImages.length)

  // ─── Main Image Generation (white bg) ───
  const mainImageGen = reactive({
    results: {},
    loading: false
  })

  // 用户在主图页手动添加的"其他"图片（不属于 A/B/C）
  const mainImageExtras = reactive([])
  let _extraIdSeq = 1
  const getMainImageKeyExtra = (id) => `extra-${id}`

  const addMainImageExtra = async (file) => {
    if (!file) return
    const compressed = await compressImage(file)
    if (!compressed) return
    const id = `${Date.now()}-${_extraIdSeq++}`
    mainImageExtras.push({ id, url: compressed, originalUrl: '' })
    saveState()
  }

  const removeMainImageExtra = (id) => {
    const idx = mainImageExtras.findIndex(e => e.id === id)
    if (idx < 0) return
    mainImageExtras.splice(idx, 1)
    const key = getMainImageKeyExtra(id)
    if (mainImageGen.results[key]) delete mainImageGen.results[key]
    saveState()
  }

  const applyCropToMainImageExtra = (id, croppedBase64) => {
    const extra = mainImageExtras.find(e => e.id === id)
    if (!extra) return
    if (!extra.originalUrl) extra.originalUrl = extra.url
    extra.url = croppedBase64
    saveState()
  }

  const restoreMainImageExtra = (id) => {
    const extra = mainImageExtras.find(e => e.id === id)
    if (!extra?.originalUrl) return
    extra.url = extra.originalUrl
    extra.originalUrl = ''
    saveState()
  }

  const generateOneMainImageExtra = (id) => {
    const extra = mainImageExtras.find(e => e.id === id)
    if (!extra?.url) return
    return generateOneMainImage(getMainImageKeyExtra(id), extra.url)
  }

  const clearAllMainImageExtras = () => {
    for (const extra of [...mainImageExtras]) {
      const key = getMainImageKeyExtra(extra.id)
      if (mainImageGen.results[key]) delete mainImageGen.results[key]
    }
    mainImageExtras.splice(0, mainImageExtras.length)
    saveState()
  }

  const getExtrasAllImageUrls = () => {
    const urls = []
    for (const extra of mainImageExtras) {
      const key = getMainImageKeyExtra(extra.id)
      const r = mainImageGen.results[key]
      if (r?.url) urls.push({ url: r.url, name: `其他_${extra.id}.png` })
    }
    return urls
  }

  const downloadExtrasZip = async () => {
    const items = getExtrasAllImageUrls()
    if (items.length === 0) {
      alert('当前没有可下载的图片')
      return
    }
    const response = await requestRaw('/api/download-zip', {
      method: 'POST',
      body: { urls: items.map(i => i.url), filenames: items.map(i => i.name) }
    })
    if (!response.ok) throw new Error('下载失败')
    const blob = await response.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = '其他_全部图片.zip'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  // 用户在裂变页手动添加的"其他"裂变任务（不属于 A/B/C）
  // 每条 extra 共享一个颜色参考/细节参考，可挂载多张原宣传图（每张对应一个裂变结果）
  const fissionExtras = reactive([])
  let _fissionExtraSeq = 1
  let _fissionExtraPromoSeq = 1
  const createFissionExtraSlot = () => ({ url: '', loading: false, progress: 0, error: '' })

  const addFissionExtra = () => {
    const id = `${Date.now()}-${_fissionExtraSeq++}`
    fissionExtras.push({
      id,
      refImage: '',
      detailImage: '',
      promos: [{
        id: `${Date.now()}-p${_fissionExtraPromoSeq++}`,
        baseImage: '',
        result: createFissionExtraSlot()
      }]
    })
    saveState()
  }

  const addFissionExtraPromo = (extraId) => {
    const extra = fissionExtras.find(e => e.id === extraId)
    if (!extra) return
    extra.promos.push({
      id: `${Date.now()}-p${_fissionExtraPromoSeq++}`,
      baseImage: '',
      result: createFissionExtraSlot()
    })
    saveState()
  }

  const removeFissionExtraPromo = (extraId, promoId) => {
    const extra = fissionExtras.find(e => e.id === extraId)
    if (!extra) return
    if (extra.promos.length <= 1) return
    const idx = extra.promos.findIndex(p => p.id === promoId)
    if (idx >= 0) extra.promos.splice(idx, 1)
    saveState()
  }

  const removeFissionExtra = (id) => {
    const idx = fissionExtras.findIndex(e => e.id === id)
    if (idx < 0) return
    fissionExtras.splice(idx, 1)
    saveState()
  }

  const setFissionExtraImage = async (extraId, field, file, promoId = null) => {
    const extra = fissionExtras.find(e => e.id === extraId)
    if (!extra || !file) return
    const compressed = await compressImage(file)
    if (!compressed) return
    if (field === 'refImage' || field === 'detailImage') {
      extra[field] = compressed
    } else if (field === 'baseImage') {
      const promo = extra.promos.find(p => p.id === promoId)
      if (promo) promo.baseImage = compressed
    }
    saveState()
  }

  const clearFissionExtraImage = (extraId, field, promoId = null) => {
    const extra = fissionExtras.find(e => e.id === extraId)
    if (!extra) return
    if (field === 'refImage' || field === 'detailImage') {
      extra[field] = ''
    } else if (field === 'baseImage') {
      const promo = extra.promos.find(p => p.id === promoId)
      if (promo) promo.baseImage = ''
    }
    saveState()
  }

  const clearFissionExtraResult = (extraId, promoId) => {
    const extra = fissionExtras.find(e => e.id === extraId)
    if (!extra) return
    const promo = extra.promos.find(p => p.id === promoId)
    if (promo) promo.result = createFissionExtraSlot()
    saveState()
  }

  const generateOneFissionExtra = async (extraId, promoId) => {
    const extra = fissionExtras.find(e => e.id === extraId)
    if (!extra) return
    const promo = extra.promos.find(p => p.id === promoId)
    if (!promo) return
    if (!MOCK_MODE && !extra.refImage) return
    if (!MOCK_MODE && !promo.baseImage) return
    promo.result.loading = true
    promo.result.error = ''
    promo.result.progress = 0

    const duration = 20000
    const startTs = performance.now()
    let resolved = false
    let finished = false
    let pendingUrl = ''

    const finalizeSuccess = () => {
      if (finished) return
      finished = true
      clearInterval(timer)
      promo.result.progress = 100
      promo.result.url = pendingUrl
      promo.result.loading = false
      saveState()
    }

    const timer = setInterval(() => {
      const elapsed = performance.now() - startTs
      const p = Math.min(99, (elapsed / duration) * 99)
      if (!resolved) promo.result.progress = p
      else if (elapsed >= duration) finalizeSuccess()
      else promo.result.progress = p
    }, 100)

    try {
      if (MOCK_MODE) {
        await new Promise(r => setTimeout(r, 2000 + Math.random() * 3000))
        pendingUrl = mockImageUrl(`fission-extra-${extraId}-${promoId}-${Date.now()}`)
      } else {
        await loadPrompts()
        const sourceUrl = promo.baseImage || ''
        if (!sourceUrl) { promo.result.loading = false; promo.result.error = '请上传原宣传图'; clearInterval(timer); return }
        const normalizedBaseImage = await normalizeImageRefForServer(sourceUrl)
        const hasDetail = !!extra.detailImage
        const body = {
          model: settings.model,
          prompt: hasDetail ? prompts.fissionPrompt + (prompts.fissionDetailPrompt || '') : prompts.fissionPrompt,
          size: '2048x2048',
          watermark: settings.watermark,
          baseImage: normalizedBaseImage,
          productImage: extra.refImage
        }
        if (hasDetail) body.detailImage = extra.detailImage
        const response = await requestRaw('/api/fission', {
          method: 'POST',
          body
        })
        const data = await response.json()
        if (!response.ok) {
          if (data?.code === INPUT_IMAGE_SENSITIVE_CODE || String(data?.error || '').includes(INPUT_IMAGE_SENSITIVE_CODE)) {
            throw createImageGenerationError(data, '裂变失败')
          }
          if (response.status === 402) throw new Error('算力不足')
          throw createImageGenerationError(data, '裂变失败')
        }
        const first = Array.isArray(data?.data) ? data.data[0] : data?.data?.[0]
        const generatedUrl = first?.url || (first?.b64_json ? `data:image/png;base64,${first.b64_json}` : '')
        pendingUrl = await persistGeneratedImage(generatedUrl)
      }
      resolved = true
      if (performance.now() - startTs >= duration) finalizeSuccess()
    } catch (e) {
      promo.result.error = e.message || '裂变失败'
      promo.result.progress = 0
      clearInterval(timer)
      promo.result.loading = false
    } finally {
      if (!resolved && !promo.result.error) {
        clearInterval(timer)
        promo.result.loading = false
      }
    }
  }

  const generateAllForFissionExtra = async (extraId) => {
    const extra = fissionExtras.find(e => e.id === extraId)
    if (!extra || !extra.refImage) return
    await Promise.all(extra.promos.filter(p => p.baseImage).map(p => generateOneFissionExtra(extraId, p.id)))
  }

  const generateAllFissionExtras = async () => {
    await Promise.all(fissionExtras.map(e => generateAllForFissionExtra(e.id)))
  }

  const clearAllFissionExtras = () => {
    fissionExtras.splice(0, fissionExtras.length)
    saveState()
  }

  const getFissionExtrasAllImageUrls = () => {
    const urls = []
    for (const extra of fissionExtras) {
      for (const promo of extra.promos) {
        if (promo.result?.url) {
          urls.push({ url: promo.result.url, name: `其他_${extra.id}_${promo.id}.png` })
        }
      }
    }
    return urls
  }

  const downloadFissionExtrasZip = async () => {
    const items = getFissionExtrasAllImageUrls()
    if (items.length === 0) {
      alert('当前没有可下载的图片')
      return
    }
    const response = await requestRaw('/api/download-zip', {
      method: 'POST',
      body: { urls: items.map(i => i.url), filenames: items.map(i => i.name) }
    })
    if (!response.ok) throw new Error('下载失败')
    const blob = await response.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = '其他_裂变全部图片.zip'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const enterMainImageGen = () => {
    imageGen.step = 4
    mainImageGen.loading = false
  }

  const clearMainImageResult = (key) => {
    if (mainImageGen.results[key]) {
      mainImageGen.results[key] = { url: '', loading: false, progress: 0, error: '' }
      saveState()
    }
  }

  const getMainImageKey = (pi, ci) => `${pi}-fission-${imageGen.fissionColors[pi]?.[ci]?.id}`
  const getMainImageKeyPromo = (pi, si) => `promo-${pi}-${si}`

  const getProductAllImageUrls = (pi) => {
    const urls = []
    // 宣传图
    const promoSlots = imageGen.genResults[pi] || []
    promoSlots.forEach((slot, si) => {
      if (slot.url) urls.push({ url: slot.url, name: `宣传图${si + 1}.png` })
    })
    // 裂变图
    const colors = imageGen.fissionColors[pi] || []
    colors.forEach((color) => {
      (color.results || []).forEach((slot, ri) => {
        if (slot.url) urls.push({ url: slot.url, name: `${color.label}_${ri + 1}.png` })
      })
    })
    // 主图（宣传图来源）
    selectedPromoForMain.filter(s => s.pi === pi).forEach(s => {
      const key = getMainImageKeyPromo(s.pi, s.si)
      const r = mainImageGen.results[key]
      if (r?.url) urls.push({ url: r.url, name: `主图_宣传图${s.si + 1}.png` })
    })
    // 主图（裂变图来源）
    selectedFissionImages.filter(s => s.pi === pi).forEach(s => {
      const key = getMainImageKey(s.pi, s.ci)
      const r = mainImageGen.results[key]
      const label = colors[s.ci]?.label || `颜色${s.ci + 1}`
      if (r?.url) urls.push({ url: r.url, name: `主图_${label}.png` })
    })
    return urls
  }

  const downloadProductZip = async (pi) => {
    const items = getProductAllImageUrls(pi)
    if (items.length === 0) return
    const response = await requestRaw('/api/download-zip', {
      method: 'POST',
      body: { urls: items.map(i => i.url), filenames: items.map(i => i.name) }
    })
    if (!response.ok) throw new Error('下载失败')
    const blob = await response.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `${products[pi]?.name || '产品'}_全部图片.zip`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const downloadAllProductsZip = async () => {
    const allItems = []
    products.forEach((_, pi) => {
      getProductAllImageUrls(pi).forEach((item) => allItems.push({
        url: item.url,
        name: `${products[pi]?.name || '产品'}_${item.name}`
      }))
    })
    getExtrasAllImageUrls().forEach((item) => allItems.push({ url: item.url, name: item.name }))
    getFissionExtrasAllImageUrls().forEach((item) => allItems.push({ url: item.url, name: item.name }))
    if (allItems.length === 0) {
      alert('当前没有可下载的图片')
      return
    }
    const response = await requestRaw('/api/download-zip', {
      method: 'POST',
      body: { urls: allItems.map(i => i.url), filenames: allItems.map(i => i.name) }
    })
    if (!response.ok) throw new Error('下载失败')
    const blob = await response.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = '全部产品_全部图片.zip'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const getSelectedFissionImageForColor = (pi, ci) => {
    const found = selectedFissionImages.find(s => s.pi === pi && s.ci === ci)
    if (!found) return null
    const slot = imageGen.fissionColors[pi]?.[ci]?.results?.[found.ri]
    return slot?.url || null
  }

  const getSelectedPromoImageForMain = (pi, si) => {
    return imageGen.genResults[pi]?.[si]?.url || null
  }

  const generateOneMainImage = async (key, sourceUrl) => {
    if (!mainImageGen.results[key]) {
      mainImageGen.results[key] = { url: '', loading: false, progress: 0, error: '' }
    }
    const slot = mainImageGen.results[key]
    slot.loading = true
    slot.error = ''
    slot.progress = 0

    const duration = 15000
    const startTs = performance.now()
    let resolved = false
    let finished = false
    let pendingUrl = ''

    const finalize = () => {
      if (finished) return
      finished = true
      clearInterval(timer)
      slot.progress = 100
      slot.url = pendingUrl
      slot.loading = false
      saveState()
    }

    const timer = setInterval(() => {
      const elapsed = performance.now() - startTs
      const p = Math.min(99, (elapsed / duration) * 99)
      if (!resolved) slot.progress = p
      else if (elapsed >= duration) finalize()
      else slot.progress = p
    }, 100)

    try {
      if (MOCK_MODE) {
        await new Promise(r => setTimeout(r, 1500 + Math.random() * 2000))
        pendingUrl = mockImageUrl(`main-${key}-${Date.now()}`)
      } else {
        if (!sourceUrl) throw new Error('未选择图片')
        const normalized = await normalizeImageRefForServer(sourceUrl)
        const body = { model: settings.model, background: 'white' }
        if (normalized.startsWith('https://')) {
          body.image = normalized
        } else {
          body.imageBase64 = normalized
        }
        const response = await requestRaw('/api/remove-bg', {
          method: 'POST',
          body
        })
        const data = await response.json()
        if (!response.ok) {
          if (data?.code === INPUT_IMAGE_SENSITIVE_CODE || String(data?.error || '').includes(INPUT_IMAGE_SENSITIVE_CODE)) {
            throw createImageGenerationError(data, '生成失败')
          }
          if (response.status === 402) throw new Error('算力不足')
          throw createImageGenerationError(data, '生成失败')
        }
        console.log('[remove-bg] response:', JSON.stringify(data).substring(0, 500))
        if (data?.imageBase64) {
          pendingUrl = data.imageBase64
        } else if (data?.imageUrl) {
          pendingUrl = data.imageUrl
        } else {
          const first = Array.isArray(data?.data) ? data.data[0] : data?.data?.[0]
          pendingUrl = first?.url || (first?.b64_json ? `data:image/png;base64,${first.b64_json}` : '')
        }
        pendingUrl = await persistGeneratedImage(pendingUrl)
        console.log('[remove-bg] pendingUrl length:', pendingUrl.length, 'starts with:', pendingUrl.substring(0, 50))
      }
      resolved = true
      if (performance.now() - startTs >= duration) finalize()
    } catch (e) {
      slot.error = e.message || '主图生成失败'
      slot.progress = 0
      clearInterval(timer)
      slot.loading = false
    } finally {
      if (!resolved && !slot.error) {
        clearInterval(timer)
        slot.loading = false
      }
    }
  }

  const generateAllMainImages = async () => {
    mainImageGen.loading = true
    const tasks = []
    // Promo images selected for main
    for (const sel of selectedPromoForMain) {
      const url = getSelectedPromoImageForMain(sel.pi, sel.si)
      if (url) tasks.push(generateOneMainImage(getMainImageKeyPromo(sel.pi, sel.si), url))
    }
    // Fission images selected for main
    const productIndices = [...new Set(selectedFissionImages.map(s => s.pi))]
    for (const pi of productIndices) {
      const colors = imageGen.fissionColors[pi] || []
      for (let ci = 0; ci < colors.length; ci++) {
        const hasSelection = selectedFissionImages.some(s => s.pi === pi && s.ci === ci)
        if (hasSelection) {
          const url = getSelectedFissionImageForColor(pi, ci)
          if (url) tasks.push(generateOneMainImage(getMainImageKey(pi, ci), url))
        }
      }
    }
    // Extras (other images not tied to any product)
    for (const extra of mainImageExtras) {
      if (extra.url) tasks.push(generateOneMainImage(getMainImageKeyExtra(extra.id), extra.url))
    }
    await Promise.all(tasks)
    mainImageGen.loading = false
  }

  // ─── Image Crop ───
  const applyCropResult = (type, pi, siOrKey, croppedBase64) => {
    let slot
    if (type === 'promo') {
      slot = imageGen.genResults[pi]?.[siOrKey]
    } else {
      slot = mainImageGen.results[siOrKey]
    }
    if (!slot) return
    if (!slot.originalUrl) slot.originalUrl = slot.url
    slot.url = croppedBase64
    saveState()
  }

  const restoreOriginalImage = (type, pi, siOrKey) => {
    let slot
    if (type === 'promo') {
      slot = imageGen.genResults[pi]?.[siOrKey]
    } else {
      slot = mainImageGen.results[siOrKey]
    }
    if (!slot?.originalUrl) return
    slot.url = slot.originalUrl
    slot.originalUrl = ''
    saveState()
  }

  // ─── Copywriting ───
  const createCopywritingInput = () => ({ fabric: '', sizeType: 'multi', hasSizeChart: 'yes', sellingPoints: '' })
  const copywriting = reactive({
    selectedProductIndex: 0,
    loading: [false, false, false],
    results: [null, null, null],
    errors: ['', '', ''],
    inputs: [createCopywritingInput(), createCopywritingInput(), createCopywritingInput()]
  })

  const generateCopywriting = async (productIndex) => {
    const product = products[productIndex]
    if (!product || !product.mainImage) {
      copywriting.errors[productIndex] = '请先在产品上传页上传该产品的主图'
      return
    }
    copywriting.loading[productIndex] = true
    copywriting.errors[productIndex] = ''
    copywriting.results[productIndex] = null

    try {
      if (MOCK_MODE) {
        await new Promise(r => setTimeout(r, 2000 + Math.random() * 2000))
        copywriting.results[productIndex] = { ...mockCopyResult }
      } else {
        await loadPrompts()
        const input = copywriting.inputs[productIndex]
        const sellingPoints = input.sellingPoints?.trim()
        let inputContext
        if (isWomenClothing.value) {
          inputContext = `\n\n【产品补充信息（请严格遵守）】\n- 面料材质：${input.fabric?.trim() || '未提供，请从图片自行判断'}\n- 尺码类型：${input.sizeType === 'multi' ? '多尺码（区分尺码）' : '均码（one-size）'}\n- 是否有尺码表：${input.hasSizeChart === 'yes' ? '有' : '无'}${sellingPoints ? `\n- 重点卖点（请在标题/五点描述/产品描述中重点突出）：${sellingPoints}` : ''}`
        } else {
          inputContext = sellingPoints ? `\n\n【产品补充信息（请严格遵守）】\n- 重点卖点（请在标题/五点描述/产品描述中重点突出）：${sellingPoints}` : ''
        }
        const copyKey = isWomenClothing.value ? 'copywritingPrompt' : 'copywritingPrompt_general'
        const copyBase = prompts[copyKey] || prompts.copywritingPrompt
        const prompt = copyBase + inputContext
        const response = await requestRaw('/api/copywriting', {
          method: 'POST',
          body: {
            model: 'doubao-seed-2-0-pro-260215',
            prompt,
            imageBase64: product.mainImage
          }
        })
        if (response.status === 402) throw new Error('算力不足或未登录')
        const data = await response.json()
        if (!response.ok) throw new Error(data?.error || '生成失败')
        // 后端直接返回解析好的 {title, titleZh, features, ...}
        if (data && data.title) {
          copywriting.results[productIndex] = data
        } else if (data?.data?.choices?.[0]?.message?.content) {
          const content = data.data.choices[0].message.content
          try {
            const jsonMatch = content.match(/\{[\s\S]*\}/)
            copywriting.results[productIndex] = jsonMatch ? JSON.parse(jsonMatch[0]) : { rawText: content }
          } catch {
            copywriting.results[productIndex] = { rawText: content }
          }
        } else if (data?.raw) {
          copywriting.results[productIndex] = { rawText: data.raw }
        } else {
          copywriting.results[productIndex] = { rawText: JSON.stringify(data) }
        }
      }
    } catch (e) {
      copywriting.errors[productIndex] = e.message || '文案生成失败'
    } finally {
      copywriting.loading[productIndex] = false
    }
  }

  const generateAllCopywriting = async () => {
    await Promise.all(products.map((p, i) => {
      if (p?.mainImage) return generateCopywriting(i)
    }))
  }

  // ─── Keyword Traffic Radar ───
  const calculateHotness = (volume) => {
    if (!volume || volume <= 31) {
      return Math.max(1, Math.floor((volume || 0) / 10))
    }
    const score = (Math.log10(volume) - 1.5) * (100 / 3.5)
    return Math.max(1, Math.min(100, Math.round(score)))
  }

  const getHeatColor = (value) => {
    if (value < 30) return '#60a5fa'
    if (value < 60) return '#34d399'
    if (value < 85) return '#fbbf24'
    return '#f87171'
  }

  const getHeatStatus = (value) => {
    if (value < 30) return 'COLD MARKET'
    if (value < 60) return 'STEADY FLOW'
    if (value < 85) return 'HIGH GREED'
    return 'EXTREME FRENZY'
  }

  const analyzeProductTraffic = async (productIndex) => {
    const result = copywriting.results[productIndex]
    if (!result || !result.keywords) return
    const rawPhrases = String(result.keywords).split(/[,，\n]+/)
    const keywords = [...new Set(rawPhrases.map(s => s.trim().toLowerCase()).filter(Boolean))]
    if (keywords.length === 0) return

    result.loadingVolume = true
    result.volumeReady = false
    result.keywordVolumeData = []
    result.totalVolumeCount = keywords.length
    result.currentVolumeIndex = 0
    result.volumeError = ''

    try {
      for (let i = 0; i < keywords.length; i++) {
        const keyword = keywords[i]
        result.currentVolumeIndex = i + 1
        try {
          const response = await requestRaw('/api/amazon/traffic-radar', {
            method: 'POST',
            body: { keyword }
          })
          if (response.status === 402) {
            throw new Error('算力不足，已停止查热度，请充值后重试')
          }
          const data = await response.json()
          const volume = data.volume !== undefined ? data.volume : 0
          result.keywordVolumeData.push({
            keyword,
            volume,
            hotness: calculateHotness(volume),
            updated: data.updated || 'N/A'
          })
        } catch (e) {
          if (e.message && e.message.includes('算力不足')) throw e
          // 单个词其他失败不中断其余词
        }
      }
      result.volumeReady = true
    } catch (e) {
      result.volumeError = e.message || '查热度失败'
    } finally {
      result.loadingVolume = false
    }
  }

  // ─── Size Chart ───
  const createSizeChartSlot = () => ({
    loading: false,
    combining: false,
    image: '',
    preview: '',
    bgFromStore: '',
    tableData: null,
    result: '',
    rawResult: '',
    error: '',
    combine: {
      widthRatio: 0.8,
      heightRatio: null,
      posXRatio: 0.1,
      posYRatio: null,
      opacity: 0.5,
      aspectLocked: true
    }
  })

  const sizeCharts = reactive([createSizeChartSlot(), createSizeChartSlot(), createSizeChartSlot()])

  const getSc = (pi) => sizeCharts[pi]

  const setSizeChartFile = async (pi, file) => {
    if (!file) return
    const sc = getSc(pi)
    if (sc.preview) URL.revokeObjectURL(sc.preview)
    sc.preview = URL.createObjectURL(file)
    sc.image = await compressImage(file)
    sc.tableData = null
    sc.result = ''
    sc.error = ''
    saveState()
  }

  const recombineSizeChart = async (pi) => {
    const sc = getSc(pi)
    if (!sc.rawResult) return
    sc.combining = true
    try {
      if (!sc.bgFromStore) {
        sc.result = sc.rawResult
        return
      }
      let fgBase64 = sc.rawResult
      if (sc.rawResult.startsWith('http')) {
        fgBase64 = await normalizeImageRefForServer(sc.rawResult)
      }
      let bgBase64 = sc.bgFromStore
      if (sc.bgFromStore.startsWith('http')) {
        bgBase64 = await normalizeImageRefForServer(sc.bgFromStore)
      }
      const body = { bgImage: bgBase64, fgImage: fgBase64 }
      const c = sc.combine || {}
      if (c.widthRatio != null) body.widthRatio = c.widthRatio
      if (c.heightRatio != null) body.heightRatio = c.heightRatio
      if (c.posXRatio != null) body.posXRatio = c.posXRatio
      if (c.posYRatio != null) body.posYRatio = c.posYRatio
      if (c.opacity != null) body.opacity = c.opacity
      const combineRes = await requestRaw('/api/combine-size-chart', {
        method: 'POST',
        body
      })
      const combineData = await combineRes.json()
      if (!combineRes.ok) throw new Error(combineData?.error || '合成失败')
      sc.result = combineData.result || sc.rawResult
    } catch (e) {
      sc.error = e.message || '重新合成失败'
    } finally {
      sc.combining = false
    }
  }

  const resetSizeChartCombine = (pi) => {
    const sc = getSc(pi)
    sc.combine.widthRatio = 0.8
    sc.combine.heightRatio = null
    sc.combine.posXRatio = 0.1
    sc.combine.posYRatio = null
    sc.combine.opacity = 0.5
    sc.combine.aspectLocked = true
  }

  const downloadSizeChart = async (pi) => {
    const sc = getSc(pi)
    if (!sc.rawResult) return null
    if (sc.bgFromStore) {
      await recombineSizeChart(pi)
    } else {
      sc.result = sc.rawResult
    }
    return sc.result
  }

  const selectSizeChartBg = async (pi, imageUrl) => {
    const sc = getSc(pi)
    sc.bgFromStore = imageUrl
    saveState()
  }

  const clearSizeChartBg = async (pi) => {
    const sc = getSc(pi)
    sc.bgFromStore = ''
    sc.result = sc.rawResult
    saveState()
  }

  const extractSizeData = async (pi) => {
    const sc = getSc(pi)
    if (!sc.image) {
      sc.error = '请先上传尺码图片'
      return
    }
    sc.loading = true
    sc.error = ''
    sc.tableData = null
    sc.result = ''
    sc.rawResult = ''
    sc.bgFromStore = ''
    sc.combining = false

    try {
      const response = await requestRaw('/api/analyze/size-chart', {
        method: 'POST',
        body: { imageBase64: sc.image }
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data?.error || '提取失败')
      if (!data.columns || !data.data) throw new Error('返回数据格式异常')
      sc.tableData = data
    } catch (e) {
      sc.error = e.message || '尺码数据提取失败'
    } finally {
      sc.loading = false
    }
  }

  const generateSizeChartImage = async (pi) => {
    const sc = getSc(pi)
    if (!sc.tableData) {
      sc.error = '请先提取尺码数据'
      return
    }
    sc.loading = true
    sc.error = ''

    try {
      await loadPrompts()
      const template = prompts.sizeChartImagePromptTemplate || DEFAULT_PROMPTS.sizeChartImagePromptTemplate
      const jsonData = JSON.stringify(sc.tableData)
      const promptText = template.replace('${DATA_PLACEHOLDER}', jsonData)

      const response = await requestRaw('/api/image', {
        method: 'POST',
        body: {
          model: 'doubao-seedream-5-0-pro-260628',
          prompt: promptText,
          size: '1152x864',
          watermark: false,
          imageBase64: sc.image
        }
      })
      const data = await response.json()
      if (!response.ok) {
        if (data?.code === INPUT_IMAGE_SENSITIVE_CODE || String(data?.error || '').includes(INPUT_IMAGE_SENSITIVE_CODE)) {
          throw createImageGenerationError(data, '生成失败')
        }
        if (response.status === 402) throw new Error('算力不足')
        throw createImageGenerationError(data, '生成失败')
      }

      const first = Array.isArray(data?.data) ? data.data[0] : data?.data?.[0]
      let imageUrl = first?.url || (first?.b64_json ? `data:image/png;base64,${first.b64_json}` : '')
      if (!imageUrl) throw new Error('未返回图片')
      imageUrl = await persistGeneratedImage(imageUrl)

      sc.rawResult = imageUrl
      sc.result = imageUrl
      saveState()
    } catch (e) {
      sc.error = e.message || '尺码图生成失败'
    } finally {
      sc.loading = false
    }
  }

  // ─── Clear helpers ───
  const clearProduct = (idx) => {
    if (idx < 0 || idx >= products.length) return
    const p = products[idx]
    if (p) {
      if (p.mainPreview) URL.revokeObjectURL(p.mainPreview)
      p.mainImage = ''
      p.mainPreview = ''
      p.detailPreviews.forEach(u => { if (u) URL.revokeObjectURL(u) })
      p.detailImages = []
      p.detailPreviews = []
    }
    const slots = imageGen.genResults[idx]
    if (slots) slots.forEach(s => { s.url = ''; s.error = ''; s.loading = false; s.progress = 0; s.customPrompt = ''; s.originalUrl = '' })
    imageGen.fissionColors[idx] = []
    exportImageOverrides[idx] = {}
    selectedModelImages[idx] = ''
    selectedSimpleBgColors[idx] = ''
    customPromoImages[idx] = []
    for (let i = selectedFissionSlots.length - 1; i >= 0; i--) {
      if (selectedFissionSlots[i].pi === idx) selectedFissionSlots.splice(i, 1)
    }
    for (let i = selectedFissionImages.length - 1; i >= 0; i--) {
      if (selectedFissionImages[i].pi === idx) selectedFissionImages.splice(i, 1)
    }
    for (let i = selectedPromoForMain.length - 1; i >= 0; i--) {
      if (selectedPromoForMain[i].pi === idx) selectedPromoForMain.splice(i, 1)
    }
    const prefix1 = `${idx}-`
    const prefix2 = `promo-${idx}-`
    for (const key of Object.keys(mainImageGen.results)) {
      if (key.startsWith(prefix1) || key.startsWith(prefix2)) {
        delete mainImageGen.results[key]
      }
    }
    copywriting.results[idx] = null
    copywriting.errors[idx] = ''
    copywriting.loading[idx] = false
    copywriting.inputs[idx] = createCopywritingInput()
    sizeCharts[idx] = createSizeChartSlot()
    saveState()
  }

  const clearAllTask = () => {
    for (let i = 0; i < products.length; i++) clearProduct(i)
    clearAllMainImageExtras()
    clearAllFissionExtras()
    imageGen.step = 2
    mainImageGen.loading = false
    saveNow()
  }

  const clearImageGenProduct = (idx) => {
    if (idx < 0 || idx >= products.length) return
    const slots = imageGen.genResults[idx]
    if (slots) slots.forEach(s => { s.url = ''; s.error = ''; s.loading = false; s.progress = 0; s.customPrompt = ''; s.originalUrl = '' })
    imageGen.fissionColors[idx] = []
    exportImageOverrides[idx] = {}
    selectedModelImages[idx] = ''
    selectedSimpleBgColors[idx] = ''
    customPromoImages[idx] = []
    for (let i = selectedFissionSlots.length - 1; i >= 0; i--) {
      if (selectedFissionSlots[i].pi === idx) selectedFissionSlots.splice(i, 1)
    }
    for (let i = selectedFissionImages.length - 1; i >= 0; i--) {
      if (selectedFissionImages[i].pi === idx) selectedFissionImages.splice(i, 1)
    }
    for (let i = selectedPromoForMain.length - 1; i >= 0; i--) {
      if (selectedPromoForMain[i].pi === idx) selectedPromoForMain.splice(i, 1)
    }
    const prefix1 = `${idx}-`
    const prefix2 = `promo-${idx}-`
    for (const key of Object.keys(mainImageGen.results)) {
      if (key.startsWith(prefix1) || key.startsWith(prefix2)) {
        delete mainImageGen.results[key]
      }
    }
    saveState()
  }

  // ─── Persistence ───
  const collectState = () => ({
    isWomenClothing: isWomenClothing.value,
    products: products.map(p => ({
      name: p.name,
      mainImage: p.mainImage,
      detailImages: p.detailImages
    })),
    imageGen: {
      modes: [...imageGen.modes],
      step: imageGen.step,
      genCounts: [...imageGen.genCounts],
      genResults: imageGen.genResults.map(rows => rows.map(s => ({ url: s.url, customPrompt: s.customPrompt, originalUrl: s.originalUrl }))),
      fissionColors: imageGen.fissionColors.map(colors => colors.map(c => ({
        id: c.id,
        label: c.label,
        refImage: c.refImage,
        detailImage: c.detailImage,
        results: c.results.map(r => ({ url: r.url }))
      })))
    },
    exportImageOverrides: exportImageOverrides.map(overrides => ({ ...overrides })),
    copywriting: { selectedProductIndex: copywriting.selectedProductIndex, results: [...copywriting.results], inputs: copywriting.inputs.map(i => ({ ...i })) },
    sizeCharts: sizeCharts.map(sc => ({
      image: sc.image,
      tableData: sc.tableData,
      result: sc.result,
      rawResult: sc.rawResult,
      bgFromStore: sc.bgFromStore
    })),
    selectedFissionSlots: selectedFissionSlots.map(s => ({
      pi: s.pi,
      si: s.si,
      ...(typeof s.customIdx === 'number' ? { customIdx: s.customIdx } : {})
    })),
    customPromoImages: customPromoImages.map(arr => (arr || []).map(img => ({ url: img.url, originalUrl: img.originalUrl || '' }))),
    selectedFissionImages: selectedFissionImages.map(s => ({ pi: s.pi, ci: s.ci, ri: s.ri })),
    selectedPromoForMain: selectedPromoForMain.map(s => ({ pi: s.pi, si: s.si })),
    selectedModelImages: [...selectedModelImages],
    selectedSimpleBgColors: [...selectedSimpleBgColors],
    mainImageResults: Object.fromEntries(
      Object.entries(mainImageGen.results).filter(([, v]) => v.url).map(([k, v]) => [k, { url: v.url, originalUrl: v.originalUrl }])
    ),
    mainImageExtras: mainImageExtras.map(e => ({ id: e.id, url: e.url, originalUrl: e.originalUrl || '' })),
    fissionExtras: fissionExtras.map(e => ({
      id: e.id,
      refImage: e.refImage || '',
      detailImage: e.detailImage || '',
      promos: (e.promos || []).map(p => ({
        id: p.id,
        baseImage: p.baseImage || '',
        result: { url: p.result?.url || '' }
      }))
    }))
  })

  let saveTimer = null
  const doSave = async () => { try { await idbSave(collectState()) } catch (e) { console.warn('保存失败:', e) } }
  const saveState = () => { if (_restoring) return; if (saveTimer) clearTimeout(saveTimer); saveTimer = setTimeout(doSave, 1000) }
  const saveNow = () => { if (saveTimer) { clearTimeout(saveTimer); saveTimer = null } return doSave() }

  // 兼容旧版缓存：若签名链接仍在有效期内，首次加载新版时自动转为永久本地数据。
  const localizeCachedGeneratedImages = async () => {
    const converted = new Map()
    let changed = false
    const localizeField = async (target, key) => {
      const current = target?.[key]
      if (typeof current !== 'string' || !current.startsWith('https://')) return
      try {
        let local = converted.get(current)
        if (!local) {
          local = await persistGeneratedImage(current)
          converted.set(current, local)
        }
        if (local && local !== current) {
          target[key] = local
          changed = true
        }
      } catch (e) {
        console.warn('[workbench] 旧缓存图片迁移失败，链接可能已过期:', e.message)
      }
    }

    for (const rows of imageGen.genResults) {
      for (const slot of rows || []) {
        await localizeField(slot, 'url')
        await localizeField(slot, 'originalUrl')
      }
    }
    for (const colors of imageGen.fissionColors) {
      for (const color of colors || []) {
        for (const slot of color.results || []) await localizeField(slot, 'url')
      }
    }
    for (const slot of Object.values(mainImageGen.results)) {
      await localizeField(slot, 'url')
      await localizeField(slot, 'originalUrl')
    }
    for (const extra of fissionExtras) {
      for (const promo of extra.promos || []) await localizeField(promo.result, 'url')
    }
    for (const sc of sizeCharts) {
      await localizeField(sc, 'rawResult')
      await localizeField(sc, 'result')
    }
    return changed
  }

  const restoreState = async () => {
    let localizedLegacyImages = false
    let migratedColorIds = false
    try {
      const data = await idbLoad()
      if (!data || typeof data !== 'object') return
      const state = typeof data === 'string' ? JSON.parse(data) : data
      _restoring = true

      if (state.isWomenClothing === false) isWomenClothing.value = false

      if (state.products) {
        state.products.forEach((saved, idx) => {
          if (products[idx]) {
            products[idx].mainImage = saved.mainImage || ''
            products[idx].mainPreview = saved.mainImage || ''
            products[idx].detailImages = saved.detailImages || []
            products[idx].detailPreviews = [...(saved.detailImages || [])]
          }
        })
      }

      if (state.imageGen) {
        imageGen.modes = state.imageGen.modes || ['', '', '']
        imageGen.step = state.imageGen.step || 2
        if (state.imageGen.genCounts) {
          state.imageGen.genCounts.forEach((c, i) => { imageGen.genCounts[i] = c })
        }
        if (state.imageGen.genResults) {
          state.imageGen.genResults.forEach((rows, pi) => {
            if (!rows) return
            imageGen.genResults[pi] = rows.map(saved => {
              const slot = createResultSlot()
              slot.url = saved.url || ''
              slot.customPrompt = saved.customPrompt || ''
              slot.originalUrl = saved.originalUrl || ''
              return slot
            })
          })
        }
        if (state.imageGen.fissionColors) {
          state.imageGen.fissionColors.forEach((colors, pi) => {
            if (!colors) return
            if (colors.some(c => c.id == null)) migratedColorIds = true
            imageGen.fissionColors[pi] = colors.map(c => ({
              ...createFissionColor(0),
              ...(c.id != null ? { id: c.id } : {}),
              label: c.label,
              refImage: c.refImage || '',
              refPreview: c.refImage || '',
              detailImage: c.detailImage || '',
              detailPreview: c.detailImage || '',
              results: (c.results || []).map(r => ({ ...createResultSlot(), url: r.url || '' }))
            }))
          })
        }
      }

      if (state.copywriting) {
        copywriting.selectedProductIndex = state.copywriting.selectedProductIndex || 0
        if (state.copywriting.results) {
          state.copywriting.results.forEach((r, i) => { if (r) copywriting.results[i] = r })
        }
        if (Array.isArray(state.copywriting.inputs)) {
          state.copywriting.inputs.forEach((inp, i) => {
            if (i < copywriting.inputs.length && inp) {
              copywriting.inputs[i] = { ...createCopywritingInput(), ...inp }
            }
          })
        }
      }

      if (state.sizeCharts) {
        state.sizeCharts.forEach((saved, pi) => {
          if (!sizeCharts[pi]) return
          sizeCharts[pi].image = saved.image || ''
          sizeCharts[pi].preview = saved.image || ''
          sizeCharts[pi].tableData = saved.tableData || null
          sizeCharts[pi].result = saved.result || ''
          sizeCharts[pi].rawResult = saved.rawResult || ''
          sizeCharts[pi].bgFromStore = saved.bgFromStore || ''
        })
      }
      if (state.sizeChart) {
        // migrate old single sizeChart to first slot
        sizeCharts[0].image = state.sizeChart.image || ''
        sizeCharts[0].preview = state.sizeChart.image || ''
        sizeCharts[0].tableData = state.sizeChart.tableData || null
        sizeCharts[0].result = state.sizeChart.result || ''
        sizeCharts[0].bgFromStore = state.sizeChart.bgFromStore || ''
      }

      if (state.selectedFissionSlots) {
        const sorted = [...state.selectedFissionSlots].sort((a, b) =>
          a.pi - b.pi || (a.customIdx ?? -1) - (b.customIdx ?? -1) || a.si - b.si)
        selectedFissionSlots.splice(0, selectedFissionSlots.length,
          ...sorted.map(s => ({
            pi: s.pi,
            si: s.si,
            ...(typeof s.customIdx === 'number' ? { customIdx: s.customIdx } : {}),
            key: typeof s.customIdx === 'number' ? `${s.pi}-custom-${s.customIdx}` : `${s.pi}-${s.si}`
          })))
      }
      if (state.customPromoImages && Array.isArray(state.customPromoImages)) {
        state.customPromoImages.forEach((arr, pi) => {
          if (Array.isArray(arr)) {
            customPromoImages[pi] = arr.map(img => ({
              url: img.url,
              preview: img.url,
              originalUrl: img.originalUrl || ''
            }))
          }
        })
      }
      if (state.selectedFissionImages) {
        selectedFissionImages.splice(0, selectedFissionImages.length,
          ...state.selectedFissionImages)
      }

      if (state.selectedPromoForMain) {
        selectedPromoForMain.splice(0, selectedPromoForMain.length,
          ...state.selectedPromoForMain)
      }

      if (state.mainImageResults) {
        for (const [key, val] of Object.entries(state.mainImageResults)) {
          // 旧快照用数组下标；迁移后主图随颜色 ID 保存，删除颜色不会串图。
          const legacy = key.match(/^(\d+)-(\d+)$/)
          if (legacy && !imageGen.fissionColors[Number(legacy[1])]?.[Number(legacy[2])]) continue
          const stableKey = legacy ? getMainImageKey(Number(legacy[1]), Number(legacy[2])) : key
          mainImageGen.results[stableKey] = { ...createResultSlot(), url: val.url || '', originalUrl: val.originalUrl || '' }
        }
      }
      if (Array.isArray(state.exportImageOverrides)) {
        state.exportImageOverrides.forEach((overrides, pi) => {
          if (pi < exportImageOverrides.length) exportImageOverrides[pi] = { ...(overrides || {}) }
        })
      }

      if (Array.isArray(state.mainImageExtras)) {
        mainImageExtras.splice(0, mainImageExtras.length,
          ...state.mainImageExtras.map(e => ({ id: e.id, url: e.url, originalUrl: e.originalUrl || '' })))
      }

      if (Array.isArray(state.fissionExtras)) {
        fissionExtras.splice(0, fissionExtras.length,
          ...state.fissionExtras.map(e => ({
            id: e.id,
            refImage: e.refImage || '',
            detailImage: e.detailImage || '',
            promos: (e.promos || []).map(p => ({
              id: p.id,
              baseImage: p.baseImage || '',
              result: { ...createFissionExtraSlot(), url: p.result?.url || '' }
            }))
          })))
      }

      if (state.selectedModelImages) {
        state.selectedModelImages.forEach((v, i) => { if (v) selectedModelImages[i] = v })
      }
      if (state.selectedSimpleBgColors) {
        state.selectedSimpleBgColors.forEach((v, i) => { if (v) selectedSimpleBgColors[i] = v })
      }
      localizedLegacyImages = await localizeCachedGeneratedImages()
    } catch (e) {
      console.warn('恢复状态失败:', e)
    } finally {
      _restoring = false
      if (localizedLegacyImages || migratedColorIds) await doSave()
    }
  }

  restoreState()
  _saveStateFn = saveState

  watch(
    () => JSON.stringify([
      ...imageGen.genResults.map(r => r.map(s => s.url)),
      ...imageGen.fissionColors.map(c => c.map(cl => cl.results?.map(r => r.url))),
      selectedFissionSlots.map(s => s.key),
      selectedFissionImages.map(s => `${s.pi}-${s.ci}-${s.ri}`),
      selectedPromoForMain.map(s => `${s.pi}-${s.si}`),
      Object.entries(mainImageGen.results).map(([k, v]) => `${k}:${v.url ? 1 : 0}`),
      copywriting.selectedProductIndex,
      copywriting.results.map(r => r ? JSON.stringify(r) : null),
      copywriting.inputs.map(i => JSON.stringify(i)),
      copywriting.errors,
      copywriting.loading,
      exportImageOverrides
    ]),
    () => { if (!_restoring && _saveStateFn) _saveStateFn() }
  )

  return {
    exportImageOverrides,
    sensitiveContentWarningVisible, closeSensitiveContentWarning,
    // Products
    products,
    setProductMainImage, clearProductMainImage,
    addProductDetailImages, removeProductDetailImage,
    // Image Gen
    imageGen, ratioOptions, lightbox,
    setProductMode, setGenCount,
    generateOneImageSlot, generateAllSlots, generateAllProducts,
    clearImageSlot,
    selectedModelImages, fetchRandomModelImage,
    selectedSimpleBgColors,
    // Fission
    selectedFissionSlots, toggleSlotSelection, isSlotSelected, getSelectedSlotCount, selectAllSlots, isAllSlotsSelected,
    customPromoImages, addCustomPromoImage, removeCustomPromoImage, getCustomPromoImageUrl,
    applyCropToCustomPromoImage, restoreCustomPromoImage,
    enterFissionFromSelection, addFissionColor, removeFissionColor,
    setFissionColorRef, setFissionColorDetail, generateOneFission, generateAllFissionForColor, generateAllFissionForProduct,
    // Fission image selection & Main image gen
    selectedFissionImages, toggleFissionImageSelection, isFissionImageSelected, selectedFissionImageCount,
    clearFissionResult, selectedPromoForMain, togglePromoForMain, isPromoSelectedForMain, getSelectedPromoImageForMain,
    mainImageGen, enterMainImageGen, getMainImageKey, getMainImageKeyPromo, getSelectedFissionImageForColor,
    clearMainImageResult,
    generateOneMainImage, generateAllMainImages, getProductAllImageUrls, downloadProductZip, downloadAllProductsZip,
    mainImageExtras, addMainImageExtra, removeMainImageExtra, generateOneMainImageExtra,
    applyCropToMainImageExtra, restoreMainImageExtra, getMainImageKeyExtra,
    clearAllMainImageExtras, getExtrasAllImageUrls, downloadExtrasZip,
    fissionExtras, addFissionExtra, removeFissionExtra, addFissionExtraPromo, removeFissionExtraPromo,
    setFissionExtraImage, clearFissionExtraImage,
    clearFissionExtraResult, generateOneFissionExtra, generateAllForFissionExtra, generateAllFissionExtras,
    clearAllFissionExtras, getFissionExtrasAllImageUrls, downloadFissionExtrasZip,
    // Crop
    applyCropResult, restoreOriginalImage,
    // Copywriting
    copywriting, generateCopywriting, generateAllCopywriting,
    // Clear
    clearProduct, clearImageGenProduct, clearAllTask,
    // Keyword Traffic Radar
    analyzeProductTraffic, calculateHotness, getHeatColor, getHeatStatus,
    // Size Chart
    sizeCharts, getSc, setSizeChartFile, clearSizeChartBg, selectSizeChartBg,
    extractSizeData, generateSizeChartImage,
    recombineSizeChart, resetSizeChartCombine, downloadSizeChart,
    // Shared
    compressImage, settings, prompts, loadPrompts, openLightbox, closeLightbox, lightboxPrev, lightboxNext,
    saveState, saveNow, restoreState,
    // Product type toggle
    isWomenClothing, setIsWomenClothing
  }
})
